import React, { useEffect, useState } from "react"
import { withRouter } from "react-router-dom"
import MetaTags from "react-meta-tags"
import { Container, Card, CardBody, Row, Col, Button } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import BootstrapTable from "react-bootstrap-table-next"
import TableLoader from "components/Common/TableLoader"
import { sitename } from "constants/constants"
import { getSpecialMarketsCall } from "helpers/repository"
import { showErrorMessages } from "helpers/helpers"
import SpecialMarketModal from "./SpecialMarketModal"

const StatusBadge = ({ status }) => (
  <span
    className={`badge badge-soft-${
      status?.toLowerCase() === "active" ? "success" : "secondary"
    } font-size-12`}
  >
    {status}
  </span>
)

const EmptyState = () => (
  <div className="text-center py-5">
    <i
      className="bx bx-line-chart"
      style={{ fontSize: "48px", color: "#c3cbe4" }}
    />
    <h5 className="mt-3 mb-1">No special markets found</h5>
    <p className="text-muted mb-0">
      Add a special market to get started.
    </p>
  </div>
)

const SpecialMarkets = () => {
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedMarket, setSelectedMarket] = useState(null)

  const fetchSpecialMarkets = async () => {
    setLoading(true)
    try {
      const response = await getSpecialMarketsCall({ page, per_page: 15 })
      const result = response?.data?.data || {}
      setData(result.data || [])
      setPagination(result || null)
    } catch (error) {
      showErrorMessages(error)
      setData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSpecialMarkets()
  }, [page])

  const openCreate = () => {
    setSelectedMarket(null)
    setIsModalOpen(true)
  }

  const openEdit = row => {
    setSelectedMarket(row)
    setIsModalOpen(true)
  }

  const columns = [
    { dataField: "name", text: "Name" },
    { dataField: "market_reference_id", text: "Reference ID" },
    {
      dataField: "calculation_type",
      text: "Calc. Type",
      formatter: cell =>
        cell ? cell.charAt(0).toUpperCase() + cell.slice(1) : "—",
    },
    {
      dataField: "calculation_value",
      text: "Calc. Value",
      formatter: (cell, row) =>
        cell != null
          ? `${cell}${row.calculation_type === "percentage" ? "%" : ""}`
          : "—",
    },
    {
      dataField: "settlement_condition",
      text: "Settlement Condition",
      formatter: cell => cell || "—",
    },
    {
      dataField: "active_leagues",
      text: "Leagues",
      formatter: cell => (
        <span className="badge badge-soft-info font-size-12">
          {cell?.length ?? 0} leagues
        </span>
      ),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: cell => <StatusBadge status={cell} />,
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_cell, row) => (
        <Button
          size="sm"
          color="primary"
          outline
          onClick={() => openEdit(row)}
        >
          Edit
        </Button>
      ),
    },
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{sitename}</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="CRM" breadcrumbItem="Special Markets" />
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="h4 mb-0 card-title">Special Markets</div>
                <Button color="success" size="sm" onClick={openCreate}>
                  Add Special Market
                </Button>
              </div>
              {loading ? (
                <TableLoader />
              ) : data.length === 0 ? (
                <EmptyState />
              ) : (
                <>
                  <div className="table-responsive">
                    <BootstrapTable
                      keyField="id"
                      responsive
                      bordered={false}
                      striped={false}
                      classes="table align-middle table-nowrap"
                      headerWrapperClasses="table-light"
                      columns={columns}
                      data={data}
                      rowClasses="mytable-hover-row"
                    />
                  </div>
                  {pagination && pagination.last_page > 1 && (
                    <Row className="mt-2">
                      <Col className="d-flex justify-content-end align-items-center gap-2">
                        <span className="text-muted">
                          Page {pagination.current_page} of{" "}
                          {pagination.last_page} ({pagination.total} total)
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          disabled={page <= 1}
                          onClick={() => setPage(page - 1)}
                        >
                          Prev
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          disabled={page >= pagination.last_page}
                          onClick={() => setPage(page + 1)}
                        >
                          Next
                        </button>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Container>
        <SpecialMarketModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          specialMarket={selectedMarket}
          onSaved={fetchSpecialMarkets}
        />
      </div>
    </React.Fragment>
  )
}

export default withRouter(SpecialMarkets)
